import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { ChampSelection } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { useChargement } from "@/lib/chargement";
import { api, type EnvoiCourriel, type FicheFournisseur } from "@/lib/api";

/**
 * « Envoyer par courriel » (LOT 2 bis, ticket #79) : la commande part de la boîte dédiée, au
 * contact choisi sur la fiche du fournisseur, le chargé d'affaires en copie. « Marquer envoyée »
 * reste à côté, pour les portails. L'écran ne décide rien : le serveur refuse un fournisseur
 * sans contact adressable, et son message se lit tel quel.
 */
export function EnvoiParCourriel({
  fournisseur,
  envoyer,
  dernierEchec,
}: {
  fournisseur: { id: string; nom: string };
  envoyer: (contactId: string) => Promise<boolean>;
  // Le dernier envoi a échoué : le même geste le rejoue.
  dernierEchec: boolean;
}) {
  const { donnees, erreur, enCours, recharger } = useChargement<FicheFournisseur>(
    (signal) => api.fournisseur(fournisseur.id, signal),
    [fournisseur.id],
  );
  const [contact, setContact] = useState("");
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees || enCours) return <Chargement message="Chargement des contacts…" />;

  const adressables = donnees.contacts.filter((c) => Boolean(c.email?.trim()));
  if (adressables.length === 0) {
    return (
      <p role="status" className="text-sm text-encre">
        Aucun contact de {fournisseur.nom} n'a d'adresse courriel. Ajoutez-en une sur{" "}
        <Link to={`/fournisseurs/${fournisseur.id}`} className="underline">
          la fiche du fournisseur
        </Link>
        , ou marquez la commande envoyée si vous l'avez passée autrement.
      </p>
    );
  }
  const choisi = contact || (adressables.length === 1 ? adressables[0].id : "");

  return (
    <form
      aria-label="Envoyer par courriel"
      className="flex flex-col gap-3 rounded-[6px] border border-filet px-4 py-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoiEnCours(true);
        try {
          await envoyer(choisi);
        } finally {
          setEnvoiEnCours(false);
        }
      }}
    >
      <ChampSelection
        libelle="Destinataire"
        aide="Vous êtes mis en copie. Le fournisseur répond à la boîte dédiée."
        required
        value={choisi}
        onChange={(evenement) => setContact(evenement.target.value)}
        options={[
          ...(adressables.length > 1 ? [{ valeur: "", libelle: "Choisir un contact…" }] : []),
          ...adressables.map((c) => ({
            valeur: c.id,
            libelle: `${[c.prenom, c.nom].filter(Boolean).join(" ")} – ${c.email}`,
          })),
        ]}
      />
      <div>
        <Bouton type="submit" variante="principal" disabled={!choisi || envoiEnCours}>
          <Mail className="h-4 w-4" aria-hidden />
          {envoiEnCours ? "Envoi en cours…" : dernierEchec ? "Renvoyer" : "Envoyer"}
        </Bouton>
      </div>
    </form>
  );
}

function quand(iso: string) {
  return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

/** Les tentatives d'envoi, la dernière d'abord : l'état se lit en toutes lettres. */
export function TentativesDEnvoi({ envois }: { envois: EnvoiCourriel[] }) {
  if (envois.length === 0) return null;
  return (
    <section aria-label="Envois par courriel" className="flex flex-col gap-1">
      <h2 className="text-sm font-semibold text-encre">Envois par courriel</h2>
      <ul className="flex flex-col gap-1 text-sm text-encre">
        {envois.map((envoi, rang) => (
          <li key={`${rang}-${envoi.le}`}>
            {envoi.reussi && !envoi.marquee_envoyee
              ? `Message parti à ${envoi.destinataire ?? "—"} le ${quand(envoi.le)}. ${envoi.erreur}`
              : envoi.reussi
                ? `Envoyée à ${envoi.destinataire ?? "—"} le ${quand(envoi.le)}.`
                : `Échec le ${quand(envoi.le)} (${envoi.destinataire ?? "—"}) : ${envoi.erreur ?? "erreur inconnue"}`}
          </li>
        ))}
      </ul>
    </section>
  );
}
